import {
  MENTION_TYPE_BLOCK,
  MENTION_TYPE_PAGE,
  MENTION_TYPES,
} from '@/constants/constants'
import { NestClient } from '@/nest'
import recaptcha from './components/App/RecaptchaWidget/recaptcha'

const API_ROOT = import.meta.env.VITE_API_URL

const withRecaptcha = (action = 'action') =>
  recaptcha
    .execute({ action })
    .then((token) => ({
      'g-recaptcha-token': token,
      'g-recaptcha-action': action,
    }))
    .catch((err) => console.error('Problem with recaptcha', err))

const request = (url, options) => {
  const xhr = new XMLHttpRequest()

  if (/\/projects\/(.*)\/figma/.test(url)) {
    window.octRequest = {
      url,
      xhr,
    }
  }

  const finalUrl = options.absolutePath ? url : `${API_ROOT}${url}`

  xhr.open(options.method, finalUrl, true)
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')

  if (options.json) {
    xhr.setRequestHeader('Content-Type', 'application/json')
  } else if (typeof options.body === 'string') {
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
  }

  if (options.token) {
    xhr.setRequestHeader('X-Auth-Token', options.token)
  }

  if (options.password) {
    xhr.setRequestHeader('X-Password-Token', options.password)
  }

  return new Promise((resolve, reject) => {
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        const response = processResponse(
          xhr.responseText,
          xhr.getResponseHeader('Content-Type')
        )

        if (Math.floor(xhr.status / 100) === 2) {
          resolve(response)
        } else if (/\/projects\/(.*)\/figma/.test(url)) {
          reject(
            new Error(
              JSON.stringify({
                type: 'figma-error',
                ...response,
              })
            )
          )
        } else {
          reject(response)
        }
      }
    }

    xhr.send(options.json || options.body)
  })

  function processResponse(response, type = 'text/html') {
    if (type && type.match(/\bjson\b/)) {
      if (!response) {
        return undefined
      }
      return JSON.parse(response)
    }

    return response
  }
}

function serialize(payload) {
  return join(keys(payload).map((key) => nest(key, payload[key])))

  function nest(key, value) {
    switch (true) {
      case value === null:
        return null
      case Array.isArray(value):
        return value.length > 0
          ? join(value.map((v, i) => nest(`${key}[${i}]`, v)))
          : null
      case typeof value === 'object':
        return join(keys(value).map((k) => nest(`${key}[${k}]`, value[k])))
      case value === true:
        return encode(key, 'true')
      case value === false:
        return encode(key, 'false')
      default:
        return encode(key, value)
    }
  }

  function keys(o) {
    return Object.keys(o)
  }

  function join(ary) {
    return ary.filter((n) => n).join('&')
  }

  function encode(key, value) {
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  }
}

function withAuthFetch(endpoint, options = {}, json = true) {
  if (Auth.token) {
    options = {
      ...options,
      headers: {
        ...options.headers,
        'X-Auth-Token': Auth.token,
      },
    }
  }

  if (!json) {
    options = {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  }

  return fetch(endpoint, {
    cache: 'no-cache',
    ...options,
  })
}

const destroy = (endpoint, options = {}) =>
  request(endpoint, { ...options, method: 'DELETE' })
const get = (endpoint, options = {}) =>
  request(endpoint, { ...options, method: 'GET' })
const post = (endpoint, { payload = {}, ...options }) =>
  request(endpoint, {
    ...options,
    method: 'POST',
    json: JSON.stringify(payload),
  })
const send = (endpoint, { payload = {}, ...options }) =>
  request(endpoint, {
    ...options,
    method: 'POST',
    body: serialize(payload),
  })

const patch = (endpoint, { payload = {}, ...options }) =>
  request(endpoint, {
    ...options,
    method: 'PATCH',
    body: serialize(payload),
  })
const upload = (endpoint, { payload, ...options }) => {
  const xhr = new XMLHttpRequest()
  xhr.open('POST', `${API_ROOT}${endpoint}`, true)
  xhr.timeout = 120000

  if (options.token) {
    xhr.setRequestHeader('X-Auth-Token', options.token)
  }

  const formData = new FormData()
  Object.keys(payload).forEach((key) => {
    formData.append(key, payload[key])
  })

  return new Promise((resolve, reject) => {
    xhr.onload = () => {
      if (Math.floor(xhr.status / 100) !== 2) {
        reject(new Error(`Upload failed with status ${xhr.status}`))
        return
      }

      try {
        const parsed = JSON.parse(xhr.responseText)
        resolve(parsed)
      } catch (e) {
        reject(e)
      }
    }
    xhr.onerror = reject
    xhr.ontimeout = () => reject(new Error('Upload timed out'))

    xhr.send(formData)
  })
}

const Auth = {
  token: null,
  register: (payload) =>
    withRecaptcha('register').then((response) =>
      NestClient.auth.register({ ...payload, ...response })
    ),
  login: (payload) => post('/login', { payload }),
  update: (payload) => send('/my', { token: Auth.token, payload }),
  reset: (payload) => send('/forgot', { payload }),
  avatar: (payload) =>
    payload === null
      ? request('/my/avatar', {
          method: 'DELETE',
          token: Auth.token,
        })
      : request('/my/avatar', {
          method: 'POST',
          body: payload,
          token: Auth.token,
        }),
  my: ({ signal, token }) =>
    withAuthFetch(`${API_ROOT}/my`, {
      signal,
      cache: 'no-cache',
      headers: {
        'X-Auth-Token': token,
      },
    }),
  announcement: (str) =>
    send(`/my/feature-announcement`, {
      token: Auth.token,
      payload: {
        code: str,
      },
    }),
  survey: (payload) => send('/my/survey', { token: Auth.token, payload }),
  cancel: (payload) =>
    send('/my/survey/cancel', { token: Auth.token, payload }),
  support: (payload) =>
    send('/my/support', {
      token: Auth.token,
      payload: { ...payload },
    }),
  figma: (payload) =>
    send('/integration/figma', { token: payload.token, payload }),
  disconnect: () => destroy('/integration/figma', { token: Auth.token }),
  updateEmojis: (payload) =>
    withAuthFetch(
      `${API_ROOT}/my/emojis`,
      {
        method: 'PATCH',
        body: payload,
      },
      false
    ),
  checkPassword: (payload) =>
    send('/my/check-password', { token: Auth.token, payload }),
  verifyEmail: (payload) =>
    send('/my/verify-email', { token: Auth.token, payload }),
  resendCode: (payload) =>
    get('/my/resend-code', { token: Auth.token, payload }),
}

const Messages = {
  get: ({ signal }) => withAuthFetch(`${API_ROOT}/my/messages`, { signal }),
  viewed: ({ id }) =>
    withAuthFetch(`${API_ROOT}/my/messages/${id}`, { method: 'POST' }),
}

const UpdateEmail = {
  request: (payload) =>
    send('/my/email-update/request', { token: Auth.token, payload }),
  verify: (payload) =>
    send('/my/email-update/verify', { token: Auth.token, payload }),
}

const VersionHistory = {
  get: ({ id }) => {
    return withAuthFetch(`${API_ROOT}/projects/${id}/snapshot`)
  },

  projectSnapshot: ({ id }) => {
    return withAuthFetch(`${API_ROOT}/snapshot/${id}`)
  },

  save: ({ id, payload }) => {
    return withAuthFetch(`${API_ROOT}/projects/${id}/snapshot`, {
      method: 'POST',
      body: payload,
    })
  },
  restore: ({ id }) => {
    return withAuthFetch(`${API_ROOT}/snapshot/${id}/restore`, {
      method: 'POST',
    })
  },
  rename: ({ id, payload }) => {
    return withAuthFetch(`${API_ROOT}/snapshot/${id}`, {
      method: 'PATCH',
      body: payload,
    })
  },
}

const Projects = {
  create: (payload) => post('/projects', { payload }),
  save: ({ id, ...payload }) =>
    send(`/my/projects/${id}/save`, { token: Auth.token, ...payload }),
  delete: (id) => send(`/my/projects/${id}/delete`, { token: Auth.token }),
  export: ({ id, format, variant, options }) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_ROOT}/export/${id}-${variant}.${format}`, true)
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
    xhr.responseType = 'blob'

    return new Promise((resolve, reject) => {
      xhr.onload = () => resolve(xhr.response)
      xhr.onerror = reject

      xhr.send(serialize({ options }))
    })
  },
  transfer: ({ projectId, ...payload }) =>
    send(`/my/projects/${projectId}/transfer`, { token: Auth.token, payload }),
  applyTransfer: ({ ...payload }) =>
    send(`/my/projects/transfer`, { token: Auth.token, payload }),
  access: ({ projectId, ...payload }) =>
    send(`/my/projects/${projectId}/access`, { token: Auth.token, payload }),
  updatePreview: (projectId) =>
    post(`/my/projects/${projectId}/thumbnail`, { token: Auth.token }),
  getFigmaDocument: (projectId, payload, abort = false) =>
    send(`/projects/${projectId}/figma`, { token: Auth.token, payload, abort }),
  templates: () => get('/templates', { token: Auth.token }),
  uploadCustomAvatar: ({ projectId, payload }) =>
    upload(`/projects/${projectId}/avatar/custom`, {
      token: Auth.token,
      payload,
    }),
  deleteCustomAvatar: ({ projectId }) =>
    destroy(`/projects/${projectId}/avatar/custom`, {
      token: Auth.token,
    }),
  recipients: ({ id }) =>
    get(`/projects/${id}/recipients`, { token: Auth.token }).then(
      (response) => {
        if (Array.isArray(response)) {
          return Promise.resolve(response)
        }

        return Promise.resolve([])
      }
    ),
  notification: (payload) => {
    const data = new URLSearchParams()
    data.append('recipients[0]', payload.userId)
    data.append('type', 'blockAssign')
    data.append(
      'body',
      JSON.stringify({
        page_name: payload.pageName,
        block_id: payload.blockId,
        block_name: payload.blockName,
      })
    )

    return withAuthFetch(
      `${API_ROOT}/projects/${payload.projectId}/notification`,
      {
        token: Auth.token,
        method: 'POST',
        body: data.toString(),
      },
      false
    )
  },

  /**
   * @param mentionType - mentionPage, mentionBlock, mentionStickyNote
   * @param projectId
   * @param payload
   */
  bulkNotification: (mentionType, projectId, payload) => {
    if (!MENTION_TYPES.includes(mentionType)) {
      return null
    }

    const data = new URLSearchParams()

    payload.recipients.forEach((recipient, i) => {
      data.append(`recipients[${i}]`, recipient)
    })
    data.append('type', mentionType)

    if (mentionType === MENTION_TYPE_PAGE) {
      data.append(
        'body',
        JSON.stringify({
          page_id: payload.pageId,
          page_name: payload.pageName,
        })
      )
    } else if (mentionType === MENTION_TYPE_BLOCK) {
      data.append(
        'body',
        JSON.stringify({
          block_id: payload.blockId,
          block_name: payload.blockName,
        })
      )
    }

    return withAuthFetch(
      `${API_ROOT}/projects/${projectId}/notification`,
      {
        token: Auth.token,
        method: 'POST',
        body: data.toString(),
      },
      false
    )
  },

  /**
   * @param projectId
   * @param payload
   */
  shareType: ({ projectId, ...payload }) =>
    withAuthFetch(`${API_ROOT}/projects/${projectId}/shareType`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * @param projectId
   * @param payload
   */
  linkPermission: ({ projectId, ...payload }) =>
    withAuthFetch(`${API_ROOT}/projects/${projectId}/linkPermission`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * @param projectId
   * @param payload
   */
  setProjectPassword: ({ projectId, ...payload }) =>
    withAuthFetch(`${API_ROOT}/my/projects/${projectId}/password`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * @param projectId
   * @param payload
   */
  memberPermission: ({ projectId, ...payload }) =>
    withAuthFetch(`${API_ROOT}/projects/${projectId}/memberPermission`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * @param projectId
   * @param {boolean} toggleOn
   */
  anonymousComments: (projectId, toggleOn) =>
    withAuthFetch(`${API_ROOT}/my/projects/${projectId}/anonymous-comments`, {
      method: 'POST',
      body: JSON.stringify({ anonymousComments: toggleOn }),
    }),
}

const Image = {
  upload: ({ projectId, ...payload }) =>
    upload(`/projects/${projectId}/image`, { payload, token: Auth.token }),
}

const Avatar = {
  update: ({ projectId, ...payload }) =>
    patch(`/projects/${projectId}/avatar`, { payload }),
  create: ({ projectId, ...payload }) =>
    send(`/projects/${projectId}/avatar`, { payload }),
}

const Comments = {
  create: ({ projectId, ...payload }, id) => {
    return send(`/projects/${projectId}/comments/${id}`, {
      token: Auth.token,
      password: localStorage.getItem(`pw_${projectId}`),
      payload: { ...payload },
    })
  },
  update: ({ projectId, id, ...payload }) => {
    return send(`/my/comments/${id}`, {
      token: Auth.token,
      password: localStorage.getItem(`pw_${projectId}`),
      payload: { ...payload },
    })
  },
  delete: (id) => destroy(`/my/comments/${id}`, { token: Auth.token }),
}

const Invitation = {
  create: ({ projectId, ...payload }) =>
    send(`/my/projects/${projectId}/invite`, {
      token: Auth.token,
      payload: { ...payload },
    }),
  accept: (id) =>
    post(`/my/invitations/${id}/accept`, { token: Auth.token, payload: {} }),
  delete: (id) => destroy(`/invitations/${id}`),
}

const Members = {
  delete: ({ projectId, id }) =>
    destroy(`/my/projects/${projectId}/members/${id}`, { token: Auth.token }),
}

const Team = {
  show: (id) => get(`/my/teams/${id}`, { token: Auth.token }),
  update: ({ teamId, payload }) =>
    send(`/my/teams/${teamId}`, { token: Auth.token, payload }),
  leave: (id) => destroy(`/my/teams/${id}`, { token: Auth.token }),
  createProject: ({ teamId, payload }) =>
    post(`/my/teams/${teamId}/projects`, { token: Auth.token, payload }),
  addAvatar: ({ teamId, payload }) =>
    upload(`/my/teams/${teamId}/avatar`, { token: Auth.token, payload }),
  deleteAvatar: (teamId) =>
    destroy(`/my/teams/${teamId}/avatar`, { token: Auth.token }),
}

const GoogleAnalytics = {
  connect: (projectId) =>
    post(`/my/projects/${projectId}/ga4/connect`, { token: Auth.token }),
  complete: (projectId, pendingId) =>
    post(`/my/projects/${projectId}/ga4/complete`, {
      token: Auth.token,
      payload: { pendingId },
    }),
  properties: (projectId) =>
    get(`/my/projects/${projectId}/ga4/properties`, { token: Auth.token }),
  selectProperty: (projectId, payload) =>
    post(`/my/projects/${projectId}/ga4/property`, {
      token: Auth.token,
      payload,
    }),
  disconnect: (projectId) =>
    destroy(`/my/projects/${projectId}/ga4`, { token: Auth.token }),
  refresh: (projectId) =>
    post(`/my/projects/${projectId}/ga4/refresh`, { token: Auth.token }),
  report: (projectId, pages) =>
    post(`/projects/${projectId}/ga4/report`, {
      token: Auth.token,
      payload: { pages },
      password: localStorage.getItem(`pw_${projectId}`),
    }),
}

const Import = {
  sitemap: (payload) =>
    withRecaptcha('import_sitemap').then((response) =>
      send(`${import.meta.env.VITE_GENERATOR_URL}/sitemap/import`, {
        payload: { ...payload, response_key: response['g-recaptcha-token'] },
        absolutePath: true,
      })
    ),
  seoTags: (payload) =>
    send(`/projects/${payload.projectId}/meta-crawler`, {
      token: Auth.token,
      payload: {
        url: payload.url,
      },
    }),
}

const Template = {
  copy: (id) => send(`/template/${id}/copy`, {}),
  request: (payload) =>
    send('/templates/submit', {
      payload,
    }),

  // Own templates of the personal workspace (paid plans) and of a team; the
  // Octopus gallery is Projects.templates (OCT-5044).
  listMine: () => get('/my/templates', { token: Auth.token }),
  list: (teamId) => get(`/my/teams/${teamId}/templates`, { token: Auth.token }),

  saveAsTemplate: (projectId, payload) =>
    post(`/my/projects/${projectId}/save-as-template`, {
      token: Auth.token,
      payload,
    }),

  use: (id, payload) =>
    post(`/my/templates/${id}/use`, { token: Auth.token, payload }),

  remove: (id) => destroy(`/my/templates/${id}`, { token: Auth.token }),
}

const Threads = {
  resolve: (id) => post(`/thread/${id}/resolve`, { token: Auth.token }),
  unresolve: (id) => post(`/thread/${id}/unresolve`, { token: Auth.token }),
  updateContent: (id, projectId, payload) =>
    post(`/thread/${id}/updateContent`, {
      token: Auth.token,
      payload,
      password: localStorage.getItem(`pw_${projectId}`),
    }),
  move: (id, payload) =>
    post(`/thread/${id}/move`, { token: Auth.token, payload }),
  create: (projectId, id, payload) =>
    post(`/project/${projectId}/thread/${id}`, {
      token: Auth.token,
      payload,
      password: localStorage.getItem(`pw_${projectId}`),
    }),
  delete: (id) => destroy(`/thread/${id}`, { token: Auth.token }),
  list: (projectId) => get(`/threads/${projectId}`, { token: Auth.token }),
}

export default {
  GoogleAnalytics,
  Threads,
  Projects,
  Image,
  Comments,
  Auth,
  Invitation,
  Members,
  Team,
  Import,
  Avatar,
  Template,
  VersionHistory,
  Messages,
  UpdateEmail,
}
